// The Mac Studio, through this site's own server.
//
// The site never holds the Mac Studio's address or key: every request goes to
// the GraphQL server's relay (…/ollama, language-graphql-neo4j-database/lib/
// ollamaRelay.js) with the signed-in person's token, and the server adds the
// key and passes it on — for signed-in people only, and only for the models
// the site uses. Replies stream back as they're made.
//
// Why streaming: Cloudflare's 100s time-to-first-byte limit (HTTP 524) kills
// any non-streaming inference where the origin holds the full response in
// memory before sending it. With `stream: true`, Ollama emits tokens as JSON
// chunks as soon as they're generated, so data is continuously flowing
// through the connection from the first token onward.
//
// Usage:
//   const raw = await streamOllamaGenerate({
//     body:      { model, prompt, format: 'json', options: { temperature: 0.1 } },
//     onProgress: (acc) => setMsg(`${acc.length} chars streamed`)
//   })
//   const parsed = JSON.parse(raw)
//
// For chat, or to read the stream yourself: macStudioFetch('chat', body).
const API = String(process.env.REACT_APP_DB_URI || '').replace(/\/graphql\/?$/, '')
export const MAC_STUDIO = `${API}/ollama`

/** POSTs an Ollama request ('generate' or 'chat') through the server; returns the fetch Response. */
export function macStudioFetch(endpoint, body, { signal } = {}) {
  let token = ''
  try { token = localStorage.getItem('token') || '' } catch { /* private mode */ }
  return fetch(`${MAC_STUDIO}/api/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    // Callers may pass the body already encoded.
    body: typeof body === 'string' ? body : JSON.stringify(body),
    signal,
  })
}

/** A picture from the image generator, through the server (txt2img body); returns the fetch Response. */
export function imageGenFetch(body, { signal } = {}) {
  let token = ''
  try { token = localStorage.getItem('token') || '' } catch { /* private mode */ }
  return fetch(`${API}/imagegen/txt2img`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
    signal,
  })
}

/** The models the site may use that are on the Mac Studio now ([{ name }]). */
export async function macStudioModels() {
  let token = ''
  try { token = localStorage.getItem('token') || '' } catch { /* private mode */ }
  const res = await fetch(`${MAC_STUDIO}/api/tags`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
  if (!res.ok) throw new Error(await macStudioError(res))
  const j = await res.json()
  return Array.isArray(j?.models) ? j.models : []
}

/** Why a request to the Mac Studio failed, in words the learner can act on. */
export async function macStudioError(res) {
  if (res.status === 401) return 'Sign in again to use the Mac Studio.'
  try {
    const j = await res.json()
    if (j && j.error) return j.error
  } catch { /* not JSON */ }
  return `The Mac Studio answered ${res.status}.`
}

const THINKS = /^(qwen3|gemma4)/

export async function streamOllamaGenerate({ body, onProgress, signal }) {
  // Newer Qwen and Gemma models can think before answering — slower, and the
  // reasoning mustn't reach a JSON parser — so it's switched off.
  const res = await macStudioFetch('generate', { ...body, stream: true, keep_alive: '30m', ...(THINKS.test(body.model || '') ? { think: false } : {}) }, { signal })
  if (!res.ok) throw new Error(await macStudioError(res))
  if (!res.body) throw new Error('Ollama returned no response body to stream')

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let accumulated = ''

  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? '' // hold incomplete tail line for next chunk
    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed) continue
      try {
        const obj = JSON.parse(trimmed)
        if (obj.error) throw new Error(`Ollama error: ${obj.error}`)
        if (typeof obj.response === 'string') {
          accumulated += obj.response
          if (onProgress) onProgress(accumulated, obj)
        }
      } catch (e) {
        if (e.message?.startsWith('Ollama error')) throw e
        // Otherwise it's a partial line; the next read completes it.
      }
    }
  }
  // Flush trailing buffer if Ollama omitted a final newline
  if (buffer.trim()) {
    try {
      const obj = JSON.parse(buffer.trim())
      if (typeof obj.response === 'string') accumulated += obj.response
    } catch (e) { /* ignore */ }
  }
  return accumulated
}
